import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AIAssistant from "@/components/AIAssistant";
import BasketDrawer from "@/components/BasketDrawer";
import CookieConsent from "@/components/CookieConsent";
import ConversionTools from "@/components/ConversionTools";
import { BasketProvider } from "@/context/BasketContext";
import { CCAuthProvider } from "@/context/CCAuthContext";
import HomePage from "@/pages/HomePage";
import useCineReveal from "@/hooks/useCineReveal";

const AboutPage = lazy(() => import("@/pages/AboutPage"));
const SmartMatchPage = lazy(() => import("@/pages/SmartMatchPage"));
const SystemBuilderPage = lazy(() => import("@/pages/SystemBuilderPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const ProductPageLite = lazy(() => import("@/pages/ProductPageLite"));
const ProductPageRebuild = lazy(() => import("@/pages/ProductPageRebuild"));
const ProductPagePremium = lazy(() => import("@/pages/ProductPagePremium"));
const ProductPagePro = lazy(() => import("@/pages/ProductPagePro"));
const ProductPageSmartOS = lazy(() => import("@/pages/ProductPageSmartOS"));
const ProductPageSmartOSApp = lazy(() => import("@/pages/ProductPageSmartOSApp"));
const ProductsPage = lazy(() => import("@/pages/ProductsPage"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const ReschedulePage = lazy(() => import("@/pages/ReschedulePage"));
const PartnershipPage = lazy(() => import("@/pages/PartnershipPage"));
const AIWorkforcePage = lazy(() => import("@/pages/AIWorkforcePage"));

// Command Center
const CCLogin = lazy(() => import("@/pages/CommandCenter/CCLogin"));
const CCLayout = lazy(() => import("@/pages/CommandCenter/CCLayout"));
const CCDashboard = lazy(() => import("@/pages/CommandCenter/CCDashboard"));

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      // Try multiple times since pages have lazy/motion mounts
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return true;
        }
        return false;
      };
      const interval = setInterval(() => {
        attempts += 1;
        if (tryScroll() || attempts >= 12) clearInterval(interval);
      }, 120);
      return () => clearInterval(interval);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-slate-800 border-t-blue-500 rounded-full animate-spin" />
      </div>
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        className="overflow-x-hidden"
        initial={false}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
      >
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/smartmatch" element={<SmartMatchPage />} />
            <Route path="/system-builder" element={<SystemBuilderPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/product/smart-site-lite" element={<ProductPageLite />} />
            <Route path="/product/smartsite-lite" element={<ProductPageLite />} />
            <Route path="/product/smart-site-rebuild" element={<ProductPageRebuild />} />
            <Route path="/product/smartsite-standard" element={<ProductPageRebuild />} />
            <Route path="/product/smart-site-premium" element={<ProductPagePremium />} />
            <Route path="/product/smartsite-premium" element={<ProductPagePremium />} />
            <Route path="/product/smart-site-pro" element={<ProductPagePro />} />
            <Route path="/product/smartsite-pro" element={<ProductPagePro />} />
            <Route path="/product/smart-os" element={<ProductPageSmartOS />} />
            <Route path="/product/smart-os-app" element={<ProductPageSmartOSApp />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/reschedule/:bookingId" element={<ReschedulePage />} />
            <Route path="/partnership" element={<PartnershipPage />} />
            <Route path="/ai-workforce" element={<AIWorkforcePage />} />
            
            {/* Command Center Routes */}
            <Route path="/command-center/login" element={<CCLogin />} />
            <Route path="/command-center" element={<CCLayout />}>
              <Route index element={<CCDashboard />} />
              <Route path="executive" element={<CCDashboard />} />
            </Route>
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  useCineReveal();
  return (
    <HelmetProvider>
      <BasketProvider>
        <CCAuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Command Center - No Navbar/Footer */}
              <Route path="/command-center/*" element={
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="login" element={<CCLogin />} />
                    <Route path="*" element={<CCLayout />}>
                      <Route index element={<CCDashboard />} />
                      <Route path="executive" element={<CCDashboard />} />
                    </Route>
                  </Routes>
                </Suspense>
              } />
              
              {/* Public Site - With Navbar/Footer */}
              <Route path="*" element={
                <div className="min-h-screen bg-slate-950">
                  <Navbar />
                  <BasketDrawer />
                  <AnimatedRoutes />
                  <Footer />
                  <AIAssistant />
                  <ConversionTools />
                  <CookieConsent />
                  <Toaster position="top-right" />
                </div>
              } />
            </Routes>
          </BrowserRouter>
        </CCAuthProvider>
      </BasketProvider>
    </HelmetProvider>
  );
}

export default App;
